import { ReactComponent as Looking } from '../../assets/looking.svg';
import { ChangeEvent, FormEvent, useEffect, useState, useContext } from 'react';
import Select, { StylesConfig } from 'react-select';
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { ActivityTemplate, Chatroom } from '../../models';
import { ActivityTemplateItem } from '../template-carousel/ActivityTemplateItem';
import ReactModal from 'react-modal';
import { QrcodeModal } from './QrcodeModal';
import { createChatroom } from '../../utils/api-calls/createChatroom';
import { useCreateChatroomDataGroup } from './UseCreateChatroomDataGroup';
import useLoading from '../../contexts/LoadingContext';
import { useTranslation } from 'react-i18next';
import { CustomerProfile } from '../../models/customerProfile';
import LIFFUserContext from '../../contexts/LIFFUserContext';

enum ACTIVATE_TIME {
  NOW = 'now',
  LATER = 'later',
}

function Title({ title }: { title: string }) {
  return <span className="text-group-primary font-medium">{title}</span>;
}

type RouteState = {
  templateId?: string;
  createFrom?: Chatroom;
  memberCount?: number;
};
export default function CreateChatroom() {
  const [t] = useTranslation(['chatroom']);
  const { setIsLoading } = useLoading();
  const templates = useLoaderData() as ActivityTemplate[];
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newInviteUrl, setNewInviteUrl] = useState<string>();
  const [dataGroup, setDataGroup] = useCreateChatroomDataGroup();
  const [searchParams] = useSearchParams();
  const { state } = useLocation() as {
    state: RouteState;
  };

  const navigate = useNavigate();
  const customProfile = useContext(LIFFUserContext) as CustomerProfile;
  const options = templates.map((t) => ({ value: t.id, label: t.name }));

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDataGroup(event.target.name, value);
  };

  const handleSelectChange = (selected: { value: string; label: string }) => {
    const template = templates.find((t) => t.id === selected.value);
    setDataGroup('activityTemplate', template);
  };

  useEffect(() => {
    const isABO = customProfile?.isABO;

    if (!isABO) {
      navigate('/');
    }
  }, [navigate, customProfile]);

  useEffect(() => {
    if (state && state.templateId) {
      const template = templates.find((t) => t.id === state.templateId);
      if (template) {
        setDataGroup('activityTemplate', template);
      }
    }
  }, [setDataGroup, state, templates]);

  useEffect(() => {
    if (searchParams && searchParams.get('template')) {
      const template = templates.find(
        (t) => t.id === searchParams.get('template')
      );
      if (template) {
        setDataGroup('activityTemplate', template);
      }
    }
  }, [setDataGroup, searchParams, templates]);

  const selectStyles: StylesConfig<any> = {
    dropdownIndicator: (styles) => ({ ...styles, color: '#38539A' }),
  };

  const handleFormSubmitAction = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { activateTime, roomName, activityTemplate } = dataGroup.data;

    setIsLoading(true);
    createChatroom(
      roomName,
      activityTemplate.id,
      activateTime === ACTIVATE_TIME.NOW,
      state?.createFrom?.id
    )
      .then((response) => {
        setIsLoading(false);

        const { inviteUrl } = response.data;
        setNewInviteUrl(() => inviteUrl);
        toggleModal();
      })
      .finally(() => setIsLoading(false));
  };

  const toggleModal = () => {
    setIsModalOpen((prevState) => !prevState);
  };

  const handleModalClose = () => {
    navigate('/');
  };

  return (
    <>
      <form noValidate onSubmit={handleFormSubmitAction}>
        <Looking className="mb-6 px-4 shadow-md" />

        <div className="px-4">
          <div className="flex flex-col gap-2 mb-6">
            {state?.memberCount && state.memberCount > 0 ? (
              <span className="w-full text-red-700 text-center">
                {t(
                  'create_chatroom_has_member',
                  '將從活動工作群「{{group}}」匯入 {{count}} 位成員',
                  {
                    group: state.createFrom?.name,
                    count: state.memberCount,
                  }
                )}
              </span>
            ) : (
              <></>
            )}
            <Title title={t('create_chatroom_naming', '為您的工作群命名')} />
            <input
              name="roomName"
              className="h-12 border border-gray-200 rounded py-3 px-4 placeholder:text-gray-700/70"
              placeholder={t(
                'create_chatroom_naming_placeholder',
                '寫下您的工作群名稱'
              )}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="flex flex-col gap-2 mb-6">
            <Title
              title={t('create_chatroom_activate_time_title', '啟動時間')}
            />
            <div className="flex flex-col">
              <span className="inline-flex items-center">
                <input
                  id="now"
                  className="form-radio peer mr-2 appearance-none text-group-primary bg-clip-content
                           border-group-primary border-2
                           focus:ring-0 focus:ring-offset-0
                           checked:bg-none checked:p-0.5
                           checked:border-group-primary checked:border-2
                           checked:hover:border-group-primary checked:hover:border-2"
                  type="radio"
                  name="activateTime"
                  value={ACTIVATE_TIME.NOW}
                  checked={dataGroup.data.activateTime === ACTIVATE_TIME.NOW}
                  onChange={handleInputChange}
                  required
                />
                <label htmlFor="now">
                  {t('activate_activity_start_bot_today', '今天開始機器人')}
                </label>
              </span>

              <span>
                <input
                  id="later"
                  className="form-radio peer mr-2 appearance-none text-group-primary bg-clip-content
                           border-group-primary border-2
                           focus:ring-0 focus:ring-offset-0
                           checked:bg-none checked:p-0.5
                           checked:border-group-primary checked:border-2
                           checked:hover:border-group-primary checked:hover:border-2"
                  type="radio"
                  name="activateTime"
                  value={ACTIVATE_TIME.LATER}
                  checked={dataGroup.data.activateTime === ACTIVATE_TIME.LATER}
                  onChange={handleInputChange}
                />
                <label htmlFor="later">
                  {t('activate_activity_later', '稍候啟動')}
                </label>
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-10">
            <Title
              title={t('create_chatroom_activity_title', '要發起什麼活動')}
            />
            <Select
              name="activityTemplateId"
              isSearchable={false}
              defaultValue={options.filter(
                (o) =>
                  o.value === state?.templateId ||
                  o.value === searchParams.get('template')
              )}
              placeholder={t(
                'create_chatroom_activity_placeholder',
                '選擇活動範本'
              )}
              options={options}
              styles={selectStyles}
              onChange={handleSelectChange}
            />
            {!!dataGroup.data.activityTemplate ? (
              <div className="flex justify-center">
                <ActivityTemplateItem
                  template={dataGroup.data.activityTemplate}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
          <button
            className="bg-group-primary text-white p-3 w-full rounded text-center disabled:opacity-30 disabled:cursor-not-allowed mb-6"
            disabled={!dataGroup.isValid}
          >
            {t('create_chatroom_submit', '創建工作群')}
          </button>
        </div>
      </form>
      <ReactModal
        isOpen={isModalOpen}
        overlayClassName="z-[51] fixed bg-black/[.4] inset-0 backdrop-blur-sm"
        className="flex h-full w-full justify-center items-center px-5"
        onAfterClose={handleModalClose}
      >
        <QrcodeModal toggleModal={toggleModal} newInviteUrl={newInviteUrl} />
      </ReactModal>
    </>
  );
}
